'use client';

import { useState } from 'react';

import { bestKey } from '../funGames';
import { useGameTracking } from '../useGameTracking';
import { useLocalBest } from '../useLocalBest';
import { CHALLENGES } from './robotData';

const BEST = bestKey('robot');

/** One shape for both challenge kinds, so the flow is a single state machine. */
type Phase =
  | { kind: 'checkbox' }
  | { kind: 'solving' }
  | { kind: 'passed'; note: string }
  | { kind: 'failed' }
  | { kind: 'done' };

const NotARobot = () => {
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>({ kind: 'checkbox' });
  /** Tiles ticked on a grid challenge, or the typed answer on a text one. */
  const [picked, setPicked] = useState<number[]>([]);
  const [typed, setTyped] = useState('');

  const best = useLocalBest(BEST);
  const { record } = useGameTracking();

  const challenge = CHALLENGES[index];

  const advance = (note: string) => {
    const solved = index + 1;
    record('robot', solved);

    if (solved >= CHALLENGES.length) {
      setPhase({ kind: 'done' });
      return;
    }
    setPhase({ kind: 'passed', note });
  };

  const next = () => {
    setIndex(index + 1);
    setPicked([]);
    setTyped('');
    setPhase({ kind: 'solving' });
  };

  const submitGrid = () => {
    if (challenge.kind !== 'grid') return;

    const correct =
      picked.length === challenge.answer.length &&
      challenge.answer.every((i) => picked.includes(i));

    if (correct) advance(challenge.note);
    else setPhase({ kind: 'failed' });
  };

  const submitText = () => {
    if (challenge.kind !== 'text') return;

    if (challenge.accepts(typed)) advance(challenge.note);
    else setPhase({ kind: 'failed' });
  };

  const restart = () => {
    setIndex(0);
    setPicked([]);
    setTyped('');
    setPhase({ kind: 'checkbox' });
  };

  const shell =
    'rounded-2xl border border-ds-border-standard bg-background-secondary p-5';

  if (phase.kind === 'checkbox') {
    return (
      <div>
        {/* The familiar box, rendered straight. Everything after it is not. */}
        <div className={`${shell} max-w-sm`}>
          <label className="flex cursor-pointer items-center gap-3">
            <input
              type="checkbox"
              onChange={() => setPhase({ kind: 'solving' })}
              className="h-6 w-6 cursor-pointer accent-primary"
            />
            <span className="text-sm text-textPrimary">
              I&apos;m not a robot
            </span>
          </label>
          <p className="mt-3 border-t border-ds-border-subtle pt-2 text-[10px] uppercase tracking-wide text-textGray">
            Privacy · Terms
          </p>
        </div>

        {best !== null && (
          <p className="mt-4 text-sm text-textGray">
            Best: {best}/{CHALLENGES.length} challenges cleared
          </p>
        )}
      </div>
    );
  }

  if (phase.kind === 'done') {
    return (
      <div className={`${shell} text-center`}>
        <p className="text-2xl font-bold text-textPrimary">✅ Verified</p>
        <p className="mx-auto mt-2 max-w-md text-textSecondary">
          You cleared all {CHALLENGES.length} challenges. None of them
          established that you are human, and one of them was unverifiable by
          design.
        </p>
        <button
          type="button"
          onClick={restart}
          className="mt-5 rounded-full border border-ds-border-standard px-5 py-2 text-sm font-emphasis text-textPrimary transition-colors hover:bg-background-light"
        >
          Start over
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex items-center gap-3">
        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-background-light">
          <div
            className="h-full rounded-full bg-primary transition-all duration-300"
            style={{ width: `${(index / CHALLENGES.length) * 100}%` }}
          />
        </div>
        <span className="text-sm tabular-nums text-textGray">
          {index + 1}/{CHALLENGES.length}
        </span>
      </div>

      <div className={shell}>
        {phase.kind === 'passed' ? (
          <div className="text-center">
            <p className="text-lg font-emphasis text-textPrimary">
              ✅ Verification passed
            </p>
            <p className="mt-2 text-sm italic text-textSecondary">
              {phase.note}
            </p>
            <button
              type="button"
              onClick={next}
              className="mt-4 rounded-full bg-primary px-6 py-2.5 font-emphasis text-white transition-colors hover:bg-indigo-400"
            >
              Continue
            </button>
          </div>
        ) : (
          <>
            <p className="text-sm font-emphasis text-textPrimary">
              {challenge.prompt}
            </p>

            {challenge.kind === 'grid' && (
              <>
                <div className="mt-4 grid grid-cols-3 gap-2">
                  {challenge.tiles.map((tile, i) => {
                    const on = picked.includes(i);

                    return (
                      <button
                        key={`${tile}-${i}`}
                        type="button"
                        aria-pressed={on}
                        aria-label={`Tile ${i + 1}`}
                        onClick={() =>
                          setPicked(
                            on ? picked.filter((p) => p !== i) : [...picked, i]
                          )
                        }
                        className={`flex aspect-square items-center justify-center rounded-lg border text-3xl transition-all duration-150 ${
                          on
                            ? 'scale-95 border-primary bg-primary/20'
                            : 'border-ds-border-standard bg-background-primary hover:bg-background-light'
                        }`}
                      >
                        <span aria-hidden="true">{tile}</span>
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={submitGrid}
                  className="mt-4 w-full rounded-full bg-primary px-6 py-2.5 font-emphasis text-white transition-colors hover:bg-indigo-400"
                >
                  Verify
                </button>
              </>
            )}

            {challenge.kind === 'text' && (
              <>
                {challenge.display && (
                  <p className="mt-4 select-none rounded-lg bg-background-light px-4 py-3 text-center text-xl italic tracking-widest text-textPrimary [text-shadow:1px_1px_0_rgba(127,127,127,0.6)]">
                    {challenge.display}
                  </p>
                )}
                <input
                  value={typed}
                  onChange={(event) => setTyped(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') submitText();
                  }}
                  aria-label={challenge.prompt}
                  className="mt-4 w-full rounded-lg border border-ds-border-standard bg-background-primary px-3 py-2 text-textPrimary outline-none focus:border-ds-border-strong"
                />
                <p className="mt-2 text-xs text-textGray">{challenge.hint}</p>
                <button
                  type="button"
                  onClick={submitText}
                  className="mt-4 w-full rounded-full bg-primary px-6 py-2.5 font-emphasis text-white transition-colors hover:bg-indigo-400"
                >
                  Verify
                </button>
              </>
            )}

            {phase.kind === 'failed' && (
              <p className="mt-3 text-sm text-red-500">
                That was not it. Try again — nothing is lost.
              </p>
            )}
          </>
        )}
      </div>

      {best !== null && (
        <p className="mt-4 text-sm text-textGray">
          Best: {best}/{CHALLENGES.length} challenges cleared
        </p>
      )}
    </div>
  );
};

export default NotARobot;
